import { cn, focusRing, type ToastItem, useToast } from "@simpro/ui";
import { Check, Info, X } from "lucide-react";
import { type PointerEvent, useEffect, useRef } from "react";

/** The designs' four seconds; information stays six (round 2 C27); an error until it is dismissed. */
const SETTLE_MS = 4000;
const INFO_MS = 6000;
/** How far up a toast has to be flicked to go. */
const SWIPE_PX = 32;

/**
 * The library's toasts, drawn in this app's own way (design note S2, round 2 C27): the same ink pill
 * for every kind, only the icon circle changes (an ivory ✓, a ringed i, an error-tint !). On a phone
 * and a tablet they drop in under the header; on a desktop they stack in the bottom-left corner, clear
 * of the side panel. Newer ones push the older back, up to three deep. Everything still goes through
 * `toast()` from `@simpro/ui`; only the drawing is this app's. Swipe up, or the close button on an
 * error, to dismiss. Polite, and never takes focus.
 *
 * Restyled under design note salon-booking-premium-redesign.md section 5.3 item 16: an ink pill with
 * the one soft shadow raised overlays get, semibold type, and an ivory focus ring on the dismiss.
 */
export function StackToaster() {
  const { toasts, dismiss } = useToast();
  const shown = toasts.slice(-3).reverse();

  return (
    <section
      aria-label="Notifications"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 top-[max(2.5rem,env(safe-area-inset-top))] z-[100] flex justify-center px-4 lg:top-auto lg:bottom-6 lg:px-0"
    >
      <div className="flex w-full justify-center lg:max-w-[77.5rem] lg:justify-start lg:px-10">
        <div className="grid w-full max-w-[26rem] grid-cols-1 pt-7">
        {shown.map((item, depth) => (
          <ToastPill key={item.id} item={item} depth={depth} onDismiss={dismiss} />
        ))}
        </div>
      </div>
    </section>
  );
}

const DEPTH = [
  "z-30 translate-y-0 scale-100",
  "pointer-events-none z-20 -translate-y-3 scale-95 opacity-90",
  "pointer-events-none z-10 -translate-y-6 scale-90 opacity-70",
] as const;

function ToastPill({
  item,
  depth,
  onDismiss,
}: {
  item: ToastItem;
  depth: number;
  onDismiss: (id: string) => void;
}) {
  const start = useRef<number | null>(null);
  const loud = item.tone === "danger" || item.tone === "warning";

  useEffect(() => {
    if (!Number.isFinite(item.duration)) return;
    const timer = setTimeout(() => onDismiss(item.id), Math.min(item.duration, item.tone === "info" ? INFO_MS : SETTLE_MS));
    return () => clearTimeout(timer);
  }, [item.id, item.duration, item.tone, onDismiss]);

  const swipe = {
    onPointerDown: (event: PointerEvent) => {
      start.current = event.clientY;
    },
    onPointerUp: (event: PointerEvent) => {
      if (start.current !== null && start.current - event.clientY > SWIPE_PX) onDismiss(item.id);
      start.current = null;
    },
  };

  return (
    <div
      role={item.tone === "danger" ? "alert" : undefined}
      aria-hidden={depth > 0 ? true : undefined}
      className={cn(
        "on-ink pointer-events-auto col-start-1 row-start-1 flex origin-top touch-none items-center gap-3 py-2.5 pr-2.5 pl-3 text-sm transition-[translate,scale,opacity] duration-300",
        item.description ? "rounded-card" : "rounded-full",
        "bg-ink text-on-ink shadow-soft",
        depth === 0 && "animate-drop-in",
        DEPTH[depth],
      )}
      {...swipe}
    >
      <span
        aria-hidden="true"
        className={cn(
          "grid size-7 shrink-0 place-items-center rounded-full font-semibold",
          item.tone === "success" && "bg-on-ink text-ink",
          item.tone === "info" && "text-on-ink ring-1 ring-on-ink/35 ring-inset",
          loud && "bg-error-field text-error-ink",
        )}
      >
        {item.tone === "success" ? (
          <Check className="size-4 animate-pop" strokeWidth={2.5} />
        ) : item.tone === "info" ? (
          <Info className="size-4" />
        ) : (
          "!"
        )}
      </span>
      <span className="min-w-0 flex-1 py-0.5">
        <b className="block font-semibold">{item.title}</b>
        {item.description ? (
          <span className="block text-[13px] text-on-ink/75 tabular-nums">{item.description}</span>
        ) : null}
      </span>
      {Number.isFinite(item.duration) ? null : (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => onDismiss(item.id)}
          className={cn(
            "-my-1.5 grid size-11 shrink-0 place-items-center rounded-full transition-colors hover:bg-on-ink/10",
            focusRing,
          )}
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
